<template lang="pug">
  .wrap
    page-title Your splashPage

    page-title(font-size="28px") Your Panels

    p Manage your panels here. You can rearrange the order, click to edit and add new panels.

    el-row
      el-col(:span='16' :s='24' :xs='24')
        .panel-frame
          .panel-list(v-if='hasPanels')
            .panel(v-for='(panel, panelIndex) in panelList' :key='panel.id' :style='panel.style')
              .content {{ panel.title }}

              .main-actions
                router-link(:to="{ name: 'spage:panelEdit', params: { panelId: panel.id } }")
                  el-button(type='primary') Edit

              .secondary-actions
                el-popover(placement='bottom' width='200px')
                  .panel-action(slot='reference')
                    icon(name="ellipsis-v" color='#999' scale='1.5')
                  .panel-action-menu
                    .panel-menu-item(v-if='panelIndex' @click='movePanel(panelIndex, "up")') Move up
                    .panel-menu-item(v-if='panelIndex < panelList.length - 1' @click='movePanel(panelIndex, "down")') Move down
                    .panel-menu-item(@click='confirmDeletePanel(panel.id)') Delete...

          el-card(v-else :style='{ margin: "20px" }')
            | No panels to display yet. You can add one below.

      el-col(:span='8' :s='24' :xs='24')
        el-card
          el-form(label-position='top')
            el-form-item(label='Requires log in:')
              el-radio(v-model="settings.requiresLogin" :label='true') Yes
              el-radio(v-model="settings.requiresLogin" :label='false') No
            el-form-item(label='URL:')
              div(:style='{ lineHeight: "1.5em", overflowWrap: "break-word" }')
                a(:href='splashPageUrl' target='_blank') {{ splashPageUrl }}

            el-form-item
              el-button(type='primary' size='large' :style='{ display: "block", width: "100%" }' @click='onSaveSettings') Save

    hr

    .add-panel-form
      page-title(font-size="28px") Add new panel
      panel-form(:panel='newPanel' @submit='onNewPanelSubmit' ref='newPanelForm')


</template>

<script>
import { mapGetters, mapActions } from "vuex";
import PageTitle from "@/shared/components/layout/PageTitle";
import ListHeader from "@/shared/components/ListHeader";
import CrudMixin from "@/shared/mixins/Crud";
import PanelForm from "../PanelForm";

const WEBAPP_BASE_URL = process.env.VUE_APP_WEBAPP_BASE_URL;

const getPanelAlignment = ({
  horizontalAlignment: hAlign,
  verticalAlignment: vAlign
}) => {
  const style = {};

  if (hAlign === "center") {
    style.justifyContent = "center";
  } else if (hAlign === "left") {
    style.justifyContent = "flex-start";
  } else if (hAlign === "right") {
    style.justifyContent = "flex-end";
  }

  if (vAlign === "center") {
    style.alignItems = "center";
  } else if (vAlign === "top") {
    style.alignItems = "flex-start";
  } else if (vAlign === "bottom") {
    style.alignItems = "flex-end";
  }

  return style;
};

const NEW_PANEL_DATA = {
  title: "",
  horizontalAlignment: "center",
  verticalAlignment: "center",
  bgImagePath: "",
  attachments: []
};

export default {
  mixins: [CrudMixin],
  components: { PageTitle, ListHeader, PanelForm },
  data() {
    return {
      newPanel: { ...NEW_PANEL_DATA },
      panels: [],
      settings: {}
    };
  },
  computed: {
    ...mapGetters({
      activeCompany: "auth/activeCompany"
    }),
    splashPageUrl() {
      return `${WEBAPP_BASE_URL}/splash/${this.settings.customUrl}`;
    },
    panelList() {
      return this.panels.map(panel => ({
        ...panel,
        style: {
          backgroundImage: `url(${panel.bgImageUrl})`,
          ...getPanelAlignment(panel)
        }
      }));
    },
    hasPanels() {
      return !!this.panels.length;
    }
  },
  methods: {
    ...mapActions("panels", [
      "fetchPanels",
      "createPanel",
      "deletePanel",
      "patchPanels"
    ]),
    ...mapActions("splashPages", ["fetchSettings", "patchSettings"]),
    loadPanels() {
      this.wrapLoadAction(this.fetchPanels)().then(data => {
        this.panels = data;
      });
    },
    loadSettings() {
      this.fetchSettings().then(data => {
        this.settings = data;
      });
    },
    updatePanelOrder() {
      const panelOrdering = this.panels.map((panel, index) => ({
        id: panel.id,
        position: index
      }));
      this.patchPanels({ data: panelOrdering }).then(() => {
        this.$message({ type: "success", message: "Panel order saved." });
      });
    },
    onNewPanelSubmit() {
      const newPanelData = {
        ...this.newPanel,
        company: this.activeCompany.id,
        position: this.panelList.length
      };
      this.wrapApiAction(this.createPanel)({ data: newPanelData }).then(() => {
        this.newPanel = { ...NEW_PANEL_DATA };
        this.$refs.newPanelForm.resetAttachments();
        this.loadPanels();
      });
    },
    movePanel(from, dir) {
      const list = this.panels;
      list.splice(
        dir === "up" ? from - 1 : from + 1,
        0,
        list.splice(from, 1)[0]
      );
      this.updatePanelOrder();
    },
    confirmDeletePanel(id) {
      this.$confirm(
        "This will permanently delete this panel and his attachments. Continue?",
        "Warning",
        {
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          type: "warning"
        }
      ).then(() => {
        this.deletePanel({ id }).then(() => {
          this.$message({ type: "info", message: "Panel deleted" });
          this.loadPanels();
        });
      });
    },
    onSaveSettings() {
      this.wrapApiAction(this.patchSettings)({ data: this.settings }).then(
        data => {
          this.settings = data;
        }
      );
    }
  },
  created() {
    this.loadSettings();
    this.loadPanels();
  }
};
</script>

<style scoped lang="scss">
.panel-frame {
  border: 10px solid #222;
  border-top-width: 20px;
  border-bottom-width: 40px;
  border-radius: 10px;
  background: #fff;
  margin-top: 20px;
  margin-bottom: 20px;
  max-width: 350px;
  min-height: 500px;
  padding: 10px;
}

.panel-list {
  max-height: 400px;
  overflow-y: auto;
}

.panel {
  height: 200px;
  position: relative;
  background-color: #eee;
  border: 1px solid #ddd;
  border-radius: 5px;
  display: flex;
  padding: 20px;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  margin-bottom: 25px;
}

.main-actions {
  position: absolute;
  top: 20px;
  right: 20px;
}

.secondary-actions {
  position: absolute;
  top: 20px;
  left: 20px;
}

.content {
  font-size: 22px;
  text-transform: uppercase;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.75);
  color: white;
}

.panel-action {
  cursor: pointer;
}

.add-panel-form {
  margin-top: 20px;
}

.panel-action-menu {
  font-size: 14px;
  margin: -10px;
}

.panel-menu-item {
  cursor: pointer;
  border-bottom: 1px solid #eee;
  padding: 8px 10px;

  &:last-child {
    border-bottom: 0;
  }
}
</style>
